import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import * as searchService from './search-api';
import type { IRootState } from '../../app/store';

export const getAllActiveSkills = createAsyncThunk<
    any,
    { clientId: number; ecrId: string }
>(
    'skill/activeSkills',
    async ({ clientId, ecrId }, { rejectWithValue, getState }) => {
        try {
            const { isWhitelisted } = (getState() as IRootState)
                .referentialAccess;
            const response = await searchService.getAllActiveSkills(
                clientId,
                ecrId,
                isWhitelisted
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getAllHSearchSkills = createAsyncThunk(
    'skill/h-search',
    async (payload: any, { rejectWithValue, getState }) => {
        try {
            const { isWhitelisted } = (getState() as IRootState)
                .referentialAccess;
            const response = await searchService.getAllHSearchSkills(
                payload,
                isWhitelisted
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const postAddFavoriteFilters = createAsyncThunk(
    'skill/favoriteFilters',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await searchService.postAddFavoriteFilters(
                payload
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getPEContentFromHSCS = createAsyncThunk(
    'skill/hscs-content',
    async (eid: any, { rejectWithValue, getState }) => {
        try {
            const { isWhitelisted } = (getState() as IRootState)
                .referentialAccess;
            const response: AxiosResponse<any> =
                await searchService.getPEContentFromHSCS(eid, isWhitelisted);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getFavoriteFilters = createAsyncThunk(
    'skill/getfavoriteFilters',
    async (_, { rejectWithValue }) => {
        try {
            const response = await searchService.getFavoriteFilters();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const editFavoriteFilters = createAsyncThunk(
    'skill/editfavoriteFilters',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await searchService.editFavoriteFilters(payload);
            return { ...response.data, filterContent: payload.filterContent };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getQuickLinks = createAsyncThunk(
    'skill/quickLinks',
    async (
        { courseIds, clientId }: { courseIds: string; clientId: number },
        { rejectWithValue, getState }
    ) => {
        try {
            const { isWhitelisted } = (getState() as IRootState)
                .referentialAccess;
            const response = await searchService.getQuickLinks(
                courseIds,
                clientId,
                isWhitelisted
            );
            return { status: response.status, data: response.data.data };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getPEAutoSuggest = createAsyncThunk(
    'skill/peAutoSuggest',
    async (
        {
            keyword,
            nursingSkillsClientId,
        }: { keyword: string; nursingSkillsClientId: string },
        { rejectWithValue, getState }
    ) => {
        try {
            const { isWhitelisted } = (getState() as IRootState)
                .referentialAccess;
            const response = await searchService.getPEAutoSuggestTitles(
                keyword,
                nursingSkillsClientId,
                isWhitelisted
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);
export const getRecentViewedSkills = createAsyncThunk(
    'user/recent-viewed-skills',
    async (_, { rejectWithValue }) => {
        try {
            const response = await searchService.getRecentViewedSkills();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getKeywordAutoSuggest = createAsyncThunk(
    'skill/keywordAutoSuggest',
    async (
        {
            keyword,
            nursingSkillsClientId,
        }: { keyword: string; nursingSkillsClientId: string },
        { rejectWithValue, getState }
    ) => {
        try {
            const { isWhitelisted } = (getState() as IRootState)
                .referentialAccess;
            const response = await searchService.getKeywordAutoSuggestTitles(
                keyword,
                nursingSkillsClientId,
                isWhitelisted
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getBrowseSkillsOrPatientEducations = createAsyncThunk(
    'skill/browseSkillsOrPatientEducations',
    async (payload: any, { rejectWithValue, getState }) => {
        try {
            const { isWhitelisted } = (getState() as IRootState)
                .referentialAccess;
            const response =
                await searchService.getBrowseSkillsOrPatientEducations(
                    payload,
                    isWhitelisted
                );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const getCombinedAutoSuggest = createAsyncThunk(
    'skill/combinedAutoSuggest',
    async (
        {
            keyword,
            nursingSkillsClientId,
        }: { keyword: string; nursingSkillsClientId: string },
        { rejectWithValue, getState }
    ) => {
        try {
            const { isWhitelisted } = (getState() as IRootState)
                .referentialAccess;
            const [peResult, keywordResult] = await Promise.allSettled([
                searchService.getPEAutoSuggestTitles(
                    keyword,
                    nursingSkillsClientId,
                    isWhitelisted
                ),
                searchService.getKeywordAutoSuggestTitles(
                    keyword,
                    nursingSkillsClientId,
                    isWhitelisted
                ),
            ]);
            return {
                peAutoSuggest:
                    peResult.status === 'fulfilled' ? peResult.value.data : [],
                keywordAutoSuggest:
                    keywordResult.status === 'fulfilled'
                        ? keywordResult.value.data
                        : [],
                errors: {
                    peAutoSuggest:
                        peResult.status === 'rejected'
                            ? peResult.reason?.response?.data ||
                              peResult.reason?.message
                            : null,
                    keywordAutoSuggest:
                        keywordResult.status === 'rejected'
                            ? keywordResult.reason?.response?.data ||
                              keywordResult.reason?.message
                            : null,
                },
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
